import { useEffect, useState, useCallback, useSyncExternalStore } from "react";
import type { Entity, Finding, TimelineEvent } from "@/lib/intel/mock";

export type LastScan = {
  id: string;
  target: string;
  targetType: string;
  /** Null when the investigation produced no evidence to score. */
  riskScore: number | null;
  trustScore?: number | null;
  scoreBreakdown?: unknown;
  confidence: number | null;
  sourceCount: number;
  entities: Entity[];
  findings: Finding[];
  timeline: TimelineEvent[];
  completedAt: string;
  caseId?: string;
};

export type CustodyAction =
  | "case_created"
  | "case_updated"
  | "scan_started"
  | "scan_completed"
  | "evidence_added"
  | "evidence_tagged"
  | "evidence_deleted"
  | "note_added"
  | "note_deleted"
  | "status_changed"
  | "exported";

export type CaseStatus = "active" | "pending_review" | "closed" | "archived";
export type Classification = "UNCLASSIFIED" | "RESTRICTED" | "CONFIDENTIAL" | "SECRET";

export interface Evidence {
  id: string;
  caseId: string;
  kind: "url" | "file" | "screenshot" | "ioc" | "transcript" | "doc";
  label: string;
  source: string;
  content: string;
  tags: string[];
  hash: string; // pseudo SHA-256 fingerprint
  collectedAt: string;
  collectedBy: string;
}

export interface Note {
  id: string;
  caseId: string;
  body: string;
  author: string;
  ts: string;
  pinned?: boolean;
}

export interface CustodyEvent {
  id: string;
  caseId: string;
  action: CustodyAction;
  actor: string;
  ts: string;
  ref?: string;     // related evidence/note id
  detail?: string;
  hash: string;     // chain hash linking to previous event
}

export interface Case {
  id: string;            // e.g. INV-2025-0419
  title: string;
  target: string;
  classification: Classification;
  status: CaseStatus;
  createdAt: string;
  updatedAt: string;
  owner: string;
  summary: string;
  tags: string[];
}

interface State {
  cases: Case[];
  evidence: Evidence[];
  notes: Note[];
  custody: CustodyEvent[];
  lastScan?: LastScan | null;
}

const KEY = "cario.intel.v1";
const ANALYST = "Analyst Konstantin";

function emptyState(): State {
  return { cases: [], evidence: [], notes: [], custody: [], lastScan: null };
}

function seedState(): State {
  return emptyState();
}

function pseudoHash(seed: string): string {
  // Deterministic display-only fingerprint (NOT cryptographic)
  let h = 0x811c9dc5;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  const hex = (h >>> 0).toString(16).padStart(8, "0");
  return `${hex}${hex.split("").reverse().join("")}`.slice(0, 16);
}

function chainHash(prev: string, payload: string): string {
  return pseudoHash(prev + "::" + payload);
}

function cryptoId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

// ---------- Store (subscribable, SSR-safe) ----------
const listeners = new Set<() => void>();
let cache: State | null = null;

function read(): State {
  if (cache) return cache;
  if (typeof window === "undefined") {
    cache = emptyState();
    return cache;
  }
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) { cache = JSON.parse(raw) as State; return cache; }
  } catch { /* ignore */ }
  cache = emptyState();
  try { localStorage.setItem(KEY, JSON.stringify(cache)); } catch { /* ignore */ }
  return cache;
}

// Persisting the whole state means JSON.stringify over the last scan payload
// (which can be megabytes). Doing that synchronously on every mutation froze
// the UI during a scan, so writes are coalesced and pushed off the critical
// path; in-memory state stays instantly consistent.
let persistTimer: ReturnType<typeof setTimeout> | null = null;

function persistNow() {
  if (persistTimer) { clearTimeout(persistTimer); persistTimer = null; }
  if (typeof window === "undefined" || !cache) return;
  try { localStorage.setItem(KEY, JSON.stringify(cache)); } catch { /* quota / private mode */ }
}

function schedulePersist() {
  if (typeof window === "undefined") return;
  if (persistTimer) return;
  persistTimer = setTimeout(persistNow, 400);
}

if (typeof window !== "undefined") {
  window.addEventListener("pagehide", persistNow);
  window.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") persistNow();
  });
}

function write(next: State) {
  cache = next;
  schedulePersist();
  listeners.forEach((l) => l());
}


function subscribe(fn: () => void) {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

// Cache the server snapshot — React warns (and can infinite-loop) if
// getServerSnapshot returns a fresh object on every call.
const SERVER_SNAPSHOT: State = emptyState();
function snapshotServer(): State { return SERVER_SNAPSHOT; }

function lastHash(state: State, caseId: string): string {
  const list = state.custody.filter((c) => c.caseId === caseId);
  return list.length ? list[list.length - 1].hash : "0".repeat(16);
}

function appendCustody(state: State, e: Omit<CustodyEvent, "id" | "ts" | "actor" | "hash">): CustodyEvent {
  const ts = new Date().toISOString();
  const prev = lastHash(state, e.caseId);
  const hash = chainHash(prev, `${e.action}|${ts}|${e.ref ?? ""}|${e.detail ?? ""}`);
  return { ...e, id: cryptoId(), ts, actor: ANALYST, hash };
}

function nextCaseId(state: State): string {
  const year = new Date().getFullYear();
  const n = state.cases.filter((c) => c.id.includes(String(year))).length + 420;
  return `INV-${year}-${String(n).padStart(4, "0")}`;
}

// ---------- Hook + actions ----------
export function useInvestigations() {
  const state = useSyncExternalStore(subscribe, read, snapshotServer);

  const createCase = useCallback((input: { title: string; target: string; classification?: Classification; tags?: string[] }) => {
    const cur = read();
    const id = nextCaseId(cur);
    const now = new Date().toISOString();
    const c: Case = {
      id, title: input.title.trim() || "Untitled investigation",
      target: input.target.trim(),
      classification: input.classification ?? "RESTRICTED",
      status: "active",
      createdAt: now, updatedAt: now, owner: ANALYST,
      summary: "",
      tags: input.tags ?? [],
    };
    const ev = appendCustody(cur, { caseId: id, action: "case_created", ref: id, detail: `Target: ${c.target}` });
    write({ ...cur, cases: [c, ...cur.cases], custody: [...cur.custody, ev] });
    return id;
  }, []);

  const updateCase = useCallback((id: string, patch: Partial<Case>) => {
    const cur = read();
    const next = cur.cases.map((c) => c.id === id ? { ...c, ...patch, updatedAt: new Date().toISOString() } : c);
    const detail = Object.keys(patch).join(", ");
    const action: CustodyAction = patch.status ? "status_changed" : "case_updated";
    const ev = appendCustody(cur, { caseId: id, action, ref: id, detail });
    write({ ...cur, cases: next, custody: [...cur.custody, ev] });
  }, []);

  const addEvidence = useCallback((caseId: string, input: Omit<Evidence, "id" | "caseId" | "hash" | "collectedAt" | "collectedBy">) => {
    const cur = read();
    const eid = `EV-${String(cur.evidence.length + 1).padStart(4, "0")}`;
    const ev: Evidence = {
      ...input, id: eid, caseId,
      hash: pseudoHash(eid + input.content),
      collectedAt: new Date().toISOString(), collectedBy: ANALYST,
    };
    const log = appendCustody(cur, { caseId, action: "evidence_added", ref: eid, detail: input.label });
    write({ ...cur, evidence: [...cur.evidence, ev], custody: [...cur.custody, log] });
    return eid;
  }, []);

  const tagEvidence = useCallback((evidenceId: string, tags: string[]) => {
    const cur = read();
    const target = cur.evidence.find((e) => e.id === evidenceId);
    if (!target) return;
    const next = cur.evidence.map((e) => e.id === evidenceId ? { ...e, tags } : e);
    const log = appendCustody(cur, { caseId: target.caseId, action: "evidence_tagged", ref: evidenceId, detail: tags.join(", ") || "(cleared)" });
    write({ ...cur, evidence: next, custody: [...cur.custody, log] });
  }, []);

  const deleteEvidence = useCallback((evidenceId: string) => {
    const cur = read();
    const target = cur.evidence.find((e) => e.id === evidenceId);
    if (!target) return;
    const log = appendCustody(cur, { caseId: target.caseId, action: "evidence_deleted", ref: evidenceId, detail: target.label });
    write({ ...cur, evidence: cur.evidence.filter((e) => e.id !== evidenceId), custody: [...cur.custody, log] });
  }, []);

  const addNote = useCallback((caseId: string, body: string, pinned = false) => {
    if (!body.trim()) return;
    const cur = read();
    const id = `N-${String(cur.notes.length + 1).padStart(4, "0")}`;
    const n: Note = { id, caseId, body: body.trim(), author: ANALYST, ts: new Date().toISOString(), pinned };
    const log = appendCustody(cur, { caseId, action: "note_added", ref: id, detail: body.slice(0, 80) });
    write({ ...cur, notes: [...cur.notes, n], custody: [...cur.custody, log] });
  }, []);

  const deleteNote = useCallback((noteId: string) => {
    const cur = read();
    const n = cur.notes.find((x) => x.id === noteId);
    if (!n) return;
    const log = appendCustody(cur, { caseId: n.caseId, action: "note_deleted", ref: noteId });
    write({ ...cur, notes: cur.notes.filter((x) => x.id !== noteId), custody: [...cur.custody, log] });
  }, []);

  const togglePin = useCallback((noteId: string) => {
    const cur = read();
    const n = cur.notes.find((x) => x.id === noteId);
    if (!n) return;
    write({ ...cur, notes: cur.notes.map((x) => x.id === noteId ? { ...x, pinned: !x.pinned } : x) });
  }, []);

  const logScan = useCallback((caseId: string, action: "scan_started" | "scan_completed", detail: string) => {
    const cur = read();
    if (!cur.cases.some((c) => c.id === caseId)) return;
    const log = appendCustody(cur, { caseId, action, ref: caseId, detail });
    write({ ...cur, custody: [...cur.custody, log] });
  }, []);

  const logExport = useCallback((caseId: string, detail: string) => {
    const cur = read();
    const log = appendCustody(cur, { caseId, action: "exported", ref: caseId, detail });
    write({ ...cur, custody: [...cur.custody, log] });
  }, []);

  const setLastScan = useCallback((scan: LastScan | null) => {
    const cur = read();
    // Cap what we keep in the session cache: a full deep-mesh run can be
    // several megabytes, and serialising that on every subsequent mutation is
    // what used to stall the console.
    const trimmed: LastScan | null = scan
      ? {
          ...scan,
          entities: scan.entities.slice(0, 150),
          findings: scan.findings.slice(0, 200),
          timeline: scan.timeline.slice(0, 150),
        }
      : null;
    write({ ...cur, lastScan: trimmed });
  }, []);


  const verifyChain = useCallback((caseId: string): { valid: boolean; brokenAt?: string } => {
    const list = state.custody.filter((c) => c.caseId === caseId);
    let prev = "0".repeat(16);
    for (const ev of list) {
      const expect = chainHash(prev, `${ev.action}|${ev.ts}|${ev.ref ?? ""}|${ev.detail ?? ""}`);
      if (expect !== ev.hash) return { valid: false, brokenAt: ev.id };
      prev = ev.hash;
    }
    return { valid: true };
  }, [state.custody]);

  return {
    state,
    createCase, updateCase,
    addEvidence, tagEvidence, deleteEvidence,
    addNote, deleteNote, togglePin,
    logScan, logExport, verifyChain,
    setLastScan,
  };
}

// Convenience selectors
export function useCase(caseId: string | null) {
  const { state } = useInvestigations();
  if (!caseId) return null;
  return state.cases.find((c) => c.id === caseId) ?? null;
}

// Tiny helper for client-only mounted gate (avoids hydration jitter)
export function useMounted() {
  const [m, setM] = useState(false);
  useEffect(() => setM(true), []);
  return m;
}